/**
 * BraccoTCG – Protezione toploader nel carrello
 *
 * Per ogni carta nel carrello con prezzo unitario >= soglia (10 €) mostra una
 * casella "Proteggi con toploader". Se spuntata aggiunge il prodotto nascosto
 * "Protezione toploader" (handle: protezione-toploader) con quantità pari alle
 * copie della carta, collegato tramite la proprietà nascosta _tl_variante.
 * Tiene le quantità allineate quando il cliente cambia o rimuove la carta.
 *
 * Configurazione letta da <script data-toploader-config> (snippet toploader-carrello).
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

const PROP_VARIANT = '_tl_variante';
const PROP_LABEL = 'Per la carta';
const ROOT = window.Shopify?.routes?.root || '/';

let cart = null;
let busy = false;
let pending = false;
let refreshTimer = 0;
let decorateTimer = 0;

function getConfig() {
  const el = document.querySelector('script[data-toploader-config]');
  if (!el) return null;
  try {
    const cfg = JSON.parse(el.textContent || '');
    return cfg && cfg.variantId ? cfg : null;
  } catch {
    return null;
  }
}

function cartItemsComponents() {
  return [...document.querySelectorAll('cart-items-component')];
}

function sectionIds() {
  return [...new Set(cartItemsComponents().map((el) => el.dataset.sectionId).filter(Boolean))];
}

async function fetchCart() {
  const res = await fetch(`${ROOT}cart.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`cart.js ${res.status}`);
  return res.json();
}

async function post(path, body) {
  const ids = sectionIds();
  const payload = { ...body };
  if (ids.length) {
    payload.sections = ids.join(',');
    payload.sections_url = window.location.pathname;
  }
  const res = await fetch(`${ROOT}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok || data.errors || (data.status && data.status >= 400)) {
    throw new Error(data.description || data.message || 'Errore carrello');
  }
  return data;
}

/** Avvisa il tema (carrello, drawer, icona) che il carrello è cambiato. */
function broadcast(newCart, sections) {
  try {
    const deferred = CartLinesUpdateEvent.createPromise();
    const event = new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [],
      promise: deferred.promise,
    });
    event.__toploader = true;
    document.dispatchEvent(event);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(newCart),
      detail: {
        sections: sections || undefined,
        items: newCart.items,
        itemCount: newCart.item_count,
        source: 'toploader',
        didError: false,
      },
    });
  } catch (error) {
    console.warn('[toploader] aggiornamento di riserva', error);
    if (!sections) return;
    for (const el of cartItemsComponents()) {
      const id = el.dataset.sectionId;
      if (id && sections[id]) {
        morphSection(id, sections[id], { mode: el.dataset.drawer !== undefined ? 'hydration' : 'full' });
      }
    }
  }
}

const linkedVariant = (item) => String(item.properties?.[PROP_VARIANT] ?? '');

/** Calcola le quantità corrette delle righe toploader. */
function desiredUpdates(c, cfg) {
  const copies = new Map();
  for (const item of c.items) {
    if (item.variant_id === cfg.variantId || item.original_price < cfg.threshold) continue;
    const key = String(item.variant_id);
    copies.set(key, (copies.get(key) || 0) + item.quantity);
  }

  const updates = {};
  const seen = new Set();
  for (const item of c.items) {
    if (item.variant_id !== cfg.variantId) continue;
    const variant = linkedVariant(item);
    const want = seen.has(variant) ? 0 : copies.get(variant) || 0;
    seen.add(variant);
    if (item.quantity !== want) updates[item.key] = want;
  }
  return updates;
}

async function refresh() {
  const cfg = getConfig();
  if (!cfg) return;
  if (busy) {
    pending = true;
    return;
  }
  busy = true;
  try {
    let c = await fetchCart();
    const updates = desiredUpdates(c, cfg);
    if (Object.keys(updates).length) {
      const data = await post('cart/update.js', { updates });
      c = data;
      broadcast(data, data.sections);
    }
    cart = c;
  } catch (error) {
    console.warn('[toploader]', error);
  } finally {
    busy = false;
    decorate();
    if (pending) {
      pending = false;
      scheduleRefresh();
    }
  }
}

function scheduleRefresh() {
  clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(refresh, 300);
}

async function toggle(input) {
  const cfg = getConfig();
  if (!cfg || busy) {
    input.checked = !input.checked;
    return;
  }
  busy = true;
  input.disabled = true;
  const variant = input.dataset.cardVariant || '';

  try {
    const c = await fetchCart();
    const tlLines = c.items.filter((it) => it.variant_id === cfg.variantId && linkedVariant(it) === variant);

    if (input.checked) {
      const cardLines = c.items.filter((it) => String(it.variant_id) === variant);
      const copies = cardLines.reduce((sum, it) => sum + it.quantity, 0);
      if (copies > 0 && !tlLines.length) {
        const added = await post('cart/add.js', {
          items: [
            {
              id: cfg.variantId,
              quantity: copies,
              properties: { [PROP_LABEL]: cardLines[0].title, [PROP_VARIANT]: variant },
            },
          ],
        });
        cart = await fetchCart();
        broadcast(cart, added.sections);
      } else {
        cart = c;
      }
    } else if (tlLines.length) {
      const updates = Object.fromEntries(tlLines.map((it) => [it.key, 0]));
      const data = await post('cart/update.js', { updates });
      cart = data;
      broadcast(data, data.sections);
    } else {
      cart = c;
    }
  } catch (error) {
    console.warn('[toploader]', error);
    input.checked = !input.checked;
  } finally {
    busy = false;
    input.disabled = false;
    decorate();
    if (pending) {
      pending = false;
      scheduleRefresh();
    }
  }
}

function buildOption(variant, checked, cfg) {
  const label = document.createElement('label');
  label.className = 'toploader-opzione';

  const input = document.createElement('input');
  input.type = 'checkbox';
  input.className = 'toploader-opzione__check';
  input.dataset.toploaderToggle = '';
  input.dataset.cardVariant = variant;
  input.checked = checked;

  const text = document.createElement('span');
  text.textContent = 'Proteggi con toploader ';
  const price = document.createElement('span');
  price.className = 'toploader-opzione__prezzo';
  price.textContent = `+${cfg.priceLabel} a copia`;
  text.appendChild(price);

  label.append(input, text);
  return label;
}

function decorate() {
  const cfg = getConfig();
  if (!cfg) return;
  if (!cart) {
    scheduleRefresh();
    return;
  }

  const byKey = new Map(cart.items.map((it) => [it.key, it]));
  const protectedVariants = new Set(
    cart.items.filter((it) => it.variant_id === cfg.variantId && it.quantity > 0).map(linkedVariant)
  );

  for (const row of document.querySelectorAll('tr.cart-items__table-row[data-key]')) {
    const item = byKey.get(row.dataset.key);
    if (!item) continue;
    const existing = row.querySelector('.toploader-opzione');

    if (item.variant_id === cfg.variantId) {
      row.classList.add('cart-items__table-row--toploader');
      existing?.remove();
      continue;
    }
    row.classList.remove('cart-items__table-row--toploader');

    if (item.original_price < cfg.threshold) {
      existing?.remove();
      continue;
    }

    const variant = String(item.variant_id);
    const checked = protectedVariants.has(variant);
    if (existing) {
      const input = existing.querySelector('input');
      if (input && !input.disabled && input.checked !== checked) input.checked = checked;
      continue;
    }

    const target = row.querySelector('.cart-items__unit-price-wrapper') || row.querySelector('.cart-items__details');
    target?.appendChild(buildOption(variant, checked, cfg));
  }
}

function scheduleDecorate() {
  clearTimeout(decorateTimer);
  decorateTimer = window.setTimeout(decorate, 50);
}

if (!window.__braccoToploader) {
  window.__braccoToploader = true;

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.matches('[data-toploader-toggle]')) toggle(target);
  });

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (event.__toploader) return;
    if (event.promise) event.promise.then(scheduleRefresh, () => {});
    else scheduleRefresh();
  });

  new MutationObserver(scheduleDecorate).observe(document.body, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }
}
